import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentsService } from '@references/shared/services/environments.service';
import { PublicReferenceRequestsService } from '@references/shared/services/public/reference-requests.service';
import { PublicSignaturesService } from '@references/shared/services/public/signatures.service';
import { defaultReferencesRelationships } from '@shared/helpers/array.helper';
import { languages } from '@shared/helpers/languages.helper';
import { IAPIErrorObject } from '@shared/interfaces/api.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IMetadatum } from '@shared/interfaces/metadatum.interface';
import { IPage } from '@shared/interfaces/page.interface';
import { IReferenceRelationship } from '@shared/interfaces/reference-relationship.interface';
import { IReferenceRequest } from '@shared/interfaces/reference-request.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { ISignature } from '@shared/interfaces/signature.interface';
import { Metadatum } from '@shared/models/metadatum.model';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { HiringFirmsService } from '@shared/services/hiring-firms.service';
import { LoggerService } from '@shared/services/logger.service';
import { MetadataService } from '@shared/services/metadata.service';
import { ToastService } from '@shared/services/toast.service';
import { DuplicateReferenceValidator } from '@shared/validators/duplicateReference.validators';
import { EmailValidityValidator } from '@shared/validators/emailValidity.validators';
import { PhoneEmailRequiredValidator } from '@shared/validators/phoneEmailRequired.validators';
import { NgXCable } from 'ngx-cable';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SignaturePad } from 'ngx-signaturepad';
import { LocalStorageService, SessionStorage } from 'ngx-webstorage';
import { combineLatest, of, Subscription } from 'rxjs';
import { debounceTime, pairwise } from 'rxjs/operators';

@Component({
  selector: 'app-reference-requests',
  templateUrl: './reference-requests.component.html'
})
export class ReferenceRequestsComponent implements OnInit, OnDestroy {
  @ViewChild(SignaturePad) signaturePad: SignaturePad;

  @SessionStorage() public hiringFirm: IHiringFirm;
  @SessionStorage() public settings: ISetting;

  public isLoading = Boolean(true);

  public nbMinReferences = Number(2);

  public submittedCTA = String(
    `${this._environments.ENDPOINTS_APP}/register?returnUrl=references`
  );

  public bsConfig: any = {
    containerClass: 'v-datepicker v-datepicker-range',
    dateInputFormat: 'll',
    rangeInputFormat: 'll',
    maxDate: new Date(),
    showWeekNumbers: false,
    adaptivePosition: true
  };

  public references: any;
  public entry: FormGroup;
  public entrySignature: FormGroup;
  public items: FormArray;
  public error: IAPIErrorObject;
  public referenceRequest: Partial<IReferenceRequest>;
  public params: any = {};
  public isSubmitted = Boolean(false);
  public isSubmitting = Boolean(false);
  public isManagerOnly = Boolean(false);
  public isMultiLanguagesEnable = Boolean(false);
  public isFilled = Boolean(false);
  public relationships: any[] = [];
  public errors: any = [];

  public languages: any[] = [];
  public language = String('en');
  public pages: IPage[] = [];
  public requestPages: IPage[] = [];
  public candiateAuthorizationPages: IPage[] = [];
  public isCollapsed = Boolean(true);

  public id: string;

  private readonly constructorName: string = String(this.constructor.name);

  private channel = String('ReferenceRequestsChannel');
  private routesSubscription: Subscription;

  constructor(
    private readonly _ar: ActivatedRoute,
    private readonly _device: DeviceDetectorService,
    private readonly _environments: EnvironmentsService,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _fb: FormBuilder,
    private readonly _ls: LocalStorageService,
    private readonly _logger: LoggerService,
    private readonly _metadata: MetadataService,
    private readonly _ngcable: NgXCable,
    private readonly _publicReferenceRequests: PublicReferenceRequestsService,
    private readonly _publicSignatures: PublicSignaturesService,
    private readonly _hiringFirms: HiringFirmsService,
    private readonly _toast: ToastService,
    private readonly _translate: TranslateService,

    private readonly _emailValidityValidator: EmailValidityValidator
  ) {
    this.id = this._ar.snapshot.params.id;
    this.routesSubscription = this._ar.queryParams.subscribe((res: any) => {
      const lng = res.lng;

      if (lng) {
        this.language = lng;
      }

      this._environments.setLng(this.language, false);
    });

    this._ngcable.connect(this._commonEnvironments.ENDPOINTS_SOCKET);
    this._ngcable.create({
      channel: this.channel,
      room: this.id
    });
  }

  public get fieldsList() {
    let html = '<ul class="list-unstyled text-start m-0 p-2">';
    const fields = [];
    const entryControls = this.entry['controls'];
    const referencesControls = (this.entry.get('references') as FormGroup)[
      'controls'
    ];
    const signatureControls = this.entrySignature['controls'];

    for (const name in entryControls) {
      if (entryControls[name].invalid && name === 'isAccepted') {
        fields.push({
          label: `REFERENCES_REQUEST.FIELDS.${name.toUpperCase()}`
        });
      }
    }

    for (const name in referencesControls) {
      if (referencesControls[name].invalid) {
        const refControls = (referencesControls[name] as FormGroup)['controls'];

        for (const control in refControls) {
          if (refControls[control].invalid) {
            fields.push({
              label: `REFERENCES_REQUEST.FIELDS.${control.toUpperCase()}`,
              value: Number(name) + 1
            });
          }
        }
      }
    }

    if (entryControls['candidateAuthorization'] && entryControls['candidateAuthorization'].invalid) {
      fields.push({
        label: `REFERENCES_REQUEST.FIELDS.CANDIDATE_AUTHORIZATION`
      });
    }

    if (
      signatureControls['id'].invalid ||
      signatureControls['signature'].invalid
    ) {
      fields.push({
        label: `REFERENCE_ANSWERS.FIELDS.SIGNATURE`
      });
    }

    fields.map((f: any) => {
      this._translate.get(f.label, f).subscribe((res: string) => {
        html += `<li><i class="lnr lnr-cross"></i> ${res}</li>`;
      });
    });

    html += '</ul>';

    return html;
  }

  public get referencesFg(): FormGroup {
    return this.entry.get('references') as FormGroup;
  }

  ngOnInit(): void {
    this.createForm();
    this.createSignatureForm();

    this.error = {};

    this.referencesFg.valueChanges
      .pipe(debounceTime(500), pairwise())
      .subscribe(([_prev, next]) => {
        this._ls.store(`${this.id}-references`, next);
      });

    this.getReferenceRequest();
  }

  ngOnDestroy(): void {
    this._ngcable?.unsubscribe();
    this.routesSubscription?.unsubscribe();
  }

  public checkForm() {
    this.entry.markAllAsTouched();
  }

  public addItem(r?: IReference) {
    this.items = this.entry.get('references') as FormArray;
    this.items.push(this.createItem(r));
  }

  public deleteItem(index: number) {
    const control = this.entry.controls['references'] as FormArray;
    control.removeAt(index);
  }

  public isEmailRequired(r: FormGroup) {
    const control = r.get('email') as FormGroup;
    const validator = control.validator({} as AbstractControl);
    return validator?.required;
  }

  public isPhoneRequired(r: any) {
    const control = r.get('phone') as FormGroup;
    const validator = control.validator({} as AbstractControl);
    return validator?.required;
  }

  public onSubmit({ value }: { value: any }): void {
    this.isSubmitting = true;

    value.references.map((r: IReference) => ({
      ...r,
      language: this.language,
      relationship: this.isManagerOnly ? 'D' : r.relationship
    }));

    const url = 'POST /public/reference_requests';
    this._publicReferenceRequests.post(value.id, value.references).subscribe(
      (res: Partial<IReferenceRequest>) => {
        this._logger.info(this.constructorName, url, res);

        if (!this.referenceRequest.candidate.metadatum) {
          this.postMetadata();
        }

        if (this.settings.is_plugins_candidate_signature_enabled) {
          this.postSignature(this.entrySignature.value);
        }

        if (res.user) {
          this.submittedCTA += `&id=${res.user.id}`;
        }

        // for mobile, go back to top back
        if (window) {
          window.scroll(0, 0);
        }

        this.isSubmitted = true;
        this.isSubmitting = false;
        this._ls.clear(`${this.id}-references`);
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.errors = err.reduce((acc: any, error: any) => {
          if (error.error_data) {
            if (!acc[error.error_data.index]) {
              acc[error.error_data.index] = [];
            }

            acc[error.error_data.index].push(error);
          }
          return acc;
        }, []);

        this._toast.error(err[0].detail);
        this.isSubmitting = false;

        this.getReferenceRequest(true);
      }
    );
  }

  public updateNotApplicable(type: string, r: FormGroup): void {
    const value = r.get(`${type}_not_applicable`).value;
    const control = r.controls[type];

    if (value) {
      control.disable();
      control.setValidators([]);
    } else {
      control.setValidators([Validators.required]);
      control.enable();
    }

    r.get(type).patchValue('');
    control.updateValueAndValidity();
  }

  public updateCurrentDate(r: FormGroup): void {
    const value = r.get('isCurrent').value;
    const control = r.controls['end_date'];

    if (value) {
      control.disable();
      control.setValidators([]);
    } else {
      control.setValidators([Validators.required]);
      control.enable();
    }

    r.get('end_date').patchValue(null);
    control.updateValueAndValidity();
  }

  public drawComplete() {
    this.entrySignature.patchValue({
      id: this.referenceRequest.candidate.id,
      signature: this.signaturePad.toDataURL()
    });
  }

  public clear() {
    this.entrySignature.patchValue({ signature: null });
    this.signaturePad.clear();
  }

  private updateCandidateAuthorization(): void {
    const control = this.entry.get('candidateAuthorization');

    if (this.settings.is_candidate_authorization_enabled) {
      control.setValidators([Validators.required, Validators.pattern('true')]);
    } else {
      control.setValidators([]);
    }

    control.updateValueAndValidity();
  }

  private createForm(): void {
    this.entry = this._fb.group({
      id: [this.id, [Validators.required]],
      references: this._fb.array([]),
      isAccepted: [false, [Validators.required, Validators.pattern('true')]],
      candidateAuthorization: [false]
    });
  }

  private createSignatureForm(): void {
    this.entrySignature = this._fb.group({
      id: [''],
      signature: ['']
    });
  }

  private createItem(r?: IReference): FormGroup {
    const isReferenceDatesEnable = this.settings.is_reference_dates_enabled;

    const formGroup: FormGroup = this._fb.group(
      {
        first_name: ['', [Validators.required]],
        last_name: ['', [Validators.required]],
        title: ['', [Validators.maxLength(63)]],
        title_not_applicable: [false],
        company_name: ['', [Validators.required]],
        company_name_not_applicable: [false],
        language: [this.language || 'en'],
        email: [
          '',
          [Validators.email]
          // [this._emailValidityValidator.validates]
        ],
        phone: [''],
        countryInitial: [''],
        country_code: ['1', [Validators.required]],
        relationship: ['', [Validators.required]],
        relationship_not_applicable: [false],
        start_date: ['', isReferenceDatesEnable ? [Validators.required] : []],
        end_date: ['', isReferenceDatesEnable ? [Validators.required] : []],
        candidate_position: [''],
        isCurrent: [false, [Validators.required]]
      },
      {
        validator: [
          PhoneEmailRequiredValidator.DynamicRequired('phone', 'email', true),
          DuplicateReferenceValidator.DuplicateReference(
            'email',
            (this.referenceRequest?.candidate?.references as IReference[]) || []
          ),
          DuplicateReferenceValidator.DuplicateReference(
            'phone',
            (this.referenceRequest?.candidate?.references as IReference[]) || []
          )
        ]
      }
    );

    if (this.isManagerOnly) {
      formGroup.patchValue({
        relationship: 'D'
      });

      formGroup.get('relationship').disable();
      formGroup.get('relationship_not_applicable').disable();
    }

    if (!!r) {
      formGroup.patchValue(r);
    }

    return formGroup;
  }

  private getReferenceRequest(isRefresh?: boolean) {
    const url = `GET /reference_requests/${this.id}`;
    this._publicReferenceRequests.get(this.id).subscribe(
      (res: Partial<IReferenceRequest>) => {
        this._logger.info(this.constructorName, url, res);

        this.referenceRequest = res;

        if (!isRefresh) {
          this.isFilled = this.referenceRequest.filled;
          this.pages = this.referenceRequest.pages;

          this.requestPages = this.pages.filter(
            (p: IPage) => p.category === 'request'
          );

          this.candiateAuthorizationPages = this.pages.filter(
            (p: IPage) => p.category === 'candidate_authorization'
          );

          this.hiringFirm = this.referenceRequest.hiringFirm;
          this.settings = this.hiringFirm.settings;

          this.getReferenceRelationships();

          if (this.isFilled && this.referenceRequest.candidate) {
            const profile = this.referenceRequest.candidate.profile;

            if (profile?.is_active) {
              this.submittedCTA = `${this._environments.ENDPOINTS_APP}/references`;
            }
          } else {
            if (this.referenceRequest.candidate) {
              this.params = {
                firstName: this.referenceRequest.candidate.first_name,
                lastName: this.referenceRequest.candidate.last_name
              };
            }

            // Default manager required
            this.nbMinReferences =
              this.referenceRequest.min_manager_references > 0
                ? this.referenceRequest.min_manager_references
                : this.nbMinReferences;

            this.isManagerOnly = this.referenceRequest.is_manager_only;

            this.isMultiLanguagesEnable =
              this.settings.is_plugins_multi_languages_enabled;

            this.languages = languages.filter(
              (l: any) => this.settings.languages.indexOf(l.id) > -1
            );

            const references = this._ls.retrieve(`${this.id}-references`);
            if (!!references && references.length > 0) {
              references.map((r: IReference) => this.addItem(r));
            } else {
              for (let i = 0; i < this.nbMinReferences; i++) {
                this.addItem();
              }
            }

            this.updateCandidateAuthorization();

            this._ngcable.send({
              type: 'reference_requests_completing',
              isCompleting: true
            });

            this.setSignatureValidators();
          }

          this.isLoading = false;
        }
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.error = err[0];
        this.isLoading = false;
      }
    );
  }

  private postMetadata(): void {
    combineLatest([
      this._environments.getIPAddress(),
      of(this._device.getDeviceInfo())
    ]).subscribe(([ip, userAgent]: any[]) => {
      const ipAddress = ip && ip.ip;
      const usrAgent = userAgent && userAgent.userAgent;

      const identifiable = this.referenceRequest.candidate.id;
      const body = {
        ...userAgent,
        identifiable,
        ip_address: ipAddress,
        user_agent: usrAgent
      };

      const url = 'POST /metadata';
      this._metadata.post(new Metadatum(body).apiData).subscribe(
        (resMeta: IMetadatum) => {
          this._logger.info(this.constructorName, url, resMeta);
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
        }
      );
    });
  }

  private setSignatureValidators() {
    const id = this.entrySignature.get('id');
    const signature = this.entrySignature.get('signature');

    if (this.settings.is_plugins_candidate_signature_enabled) {
      id.setValidators([Validators.required]);
      signature.setValidators([Validators.required]);
    } else {
      id.setValidators([]);
      signature.setValidators([]);
    }
  }

  private postSignature(value: Partial<ISignature>): void {
    const url = `PATCH /signatures/${value.id}`;
    this._publicSignatures.patchSignature(value).subscribe(
      (res: ISignature) => {
        this._logger.info(this.constructorName, url, res);

        this.isSubmitted = true;
        this.isSubmitting = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.isSubmitting = false;
      }
    );
  }

  private getReferenceRelationships() {
    if (!!this.hiringFirm.id) {
      const url = `GET /hiring_firms/${this.hiringFirm.id}/reference_relationships`;
      this._hiringFirms.getReferenceRelationships(this.hiringFirm.id).subscribe(
        (res: IReferenceRelationship[]) => {
          this._logger.info(this.constructorName, url, res);
          this.relationships = res.filter(
            (r: IReferenceRelationship) => r.is_active
          );
        },
        (err) => {
          this._logger.info(this.constructorName, url, err);
        }
      );
    } else {
      this.relationships = defaultReferencesRelationships;
    }
  }
}
